import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DESIGN_TOKENS, getTypographyStyles } from '../styles/constants';
// S3 asset URL
const thankYouIcon = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/thank-you-icon.svg';

interface ThankYouPopupProps {
  open: boolean;
  onClose: () => void;
}

const ThankYouPopup: React.FC<ThankYouPopupProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
          backgroundColor: DESIGN_TOKENS.colors.background,
          boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
          maxWidth: '512px',
          width: '100%',
          margin: '24px',
          position: 'relative',
          overflow: 'visible',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          margin: '24px',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      {/* Close Button */}
      <Box
        sx={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.backgroundInput,
            borderRadius: '40px',
            width: '40px',
            height: '40px',
            padding: '8px',
            '&:hover': {
              backgroundColor: DESIGN_TOKENS.colors.backgroundInput,
              opacity: 0.8,
            },
          }}
        >
          <CloseIcon 
            sx={{ 
              fontSize: '24px',
              color: DESIGN_TOKENS.colors.text,
            }} 
          />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          padding: '80px 24px 40px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '32px',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={thankYouIcon}
            alt="Thank you"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Text Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            maxWidth: '460px',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              ...getTypographyStyles('h3'),
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
              fontSize: DESIGN_TOKENS.typography.fontSizes.h3,
              lineHeight: 1.2,
              letterSpacing: '-0.11px',
              color: DESIGN_TOKENS.colors.text,
              textAlign: 'center',
              margin: 0,
            }}
          >
            Thank you!
          </Typography>
          
          <Typography
            sx={{
              ...getTypographyStyles('body1'),
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
              fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
              lineHeight: 1.4,
              color: DESIGN_TOKENS.colors.text,
              textAlign: 'center',
              margin: 0,
            }}
          >
            Your application sent.
            <br />
            We'll contact you on review and approval.
          </Typography>
        </Box>

        {/* Done Button */}
        <Button
          onClick={onClose}
          fullWidth
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.primary,
            borderRadius: DESIGN_TOKENS.spacing.borderRadius,
            height: '56px',
            padding: '2px 32px',
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
            fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
            lineHeight: 1.5,
            letterSpacing: '-0.32px',
            color: DESIGN_TOKENS.colors.white,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            transition: DESIGN_TOKENS.transitions.smooth,
            '&:hover': {
              backgroundColor: DESIGN_TOKENS.colors.primaryDark,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouPopup;
